/***
 * Copyright (c) Myia 2023-2025 - All Rights Reserved
 */
import * as monaco from 'monaco-editor';

import { toast } from '../../ui/use-toast';
import { useTabManagerContext } from '../context/TabManagerContext';
import { isSyntaxValid } from '../helpers/codeEditorHelpers';
import { FunctionMetadata } from '../types/LiveAgentEditorTypes';

import { Step as UIStep } from '@/pages/projects/LiveAgentTypes';

export const useEditorEvents = (openBlockTabFromMetadata?: (metadata: FunctionMetadata) => Promise<void>) => {
    const { editorRef, astParser, readOnly, tabsRef, activeTabIdRef, executeStep, showSelector } =
        useTabManagerContext();

    const handleEditorKeyDown = (
        e: monaco.IKeyboardEvent,
        hasOneRunningStepOrOneInQueue: () => boolean,
        executeStepAtLine: (lineNumber: number) => void,
    ) => {
        if (readOnly) return;

        const isMac = /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
        const isCtrlEnter = (!isMac && e.ctrlKey && e.code === 'Enter') || (isMac && e.metaKey && e.code === 'Enter');

        if (isCtrlEnter) {
            e.preventDefault();
            e.stopPropagation();

            if (hasOneRunningStepOrOneInQueue()) return;

            const code = editorRef.current?.getValue() ?? '';
            if (!isSyntaxValid(code, astParser)) {
                toast({
                    title: 'Syntax error in the code editor, please fix it before running a step',
                    variant: 'destructive',
                });
                return;
            }

            const position = editorRef.current?.getPosition();
            if (!position) return;

            executeStepAtLine(position.lineNumber);
        }
    };

    const handleEditorMouseDown = (
        e: monaco.editor.IEditorMouseEvent,
        hasOneRunningStepOrOneInQueue: () => boolean,
        executeStepAtLine: (lineNumber: number) => void,
    ) => {
        if (readOnly) return;

        const t = e.target;

        // Handle glyph margin clicks (execution button)
        if (t.type === monaco.editor.MouseTargetType.GUTTER_GLYPH_MARGIN && t.position?.lineNumber) {
            if (hasOneRunningStepOrOneInQueue()) return;

            const code = editorRef.current?.getValue() ?? '';
            if (!isSyntaxValid(code, astParser)) {
                toast({
                    title: 'Syntax error in the code editor, please fix it before running a step',
                    variant: 'destructive',
                });
                return;
            }

            executeStepAtLine(t.position.lineNumber);
            return;
        }

        // Handle step into button clicks (check for specific element class)
        if (t.type === monaco.editor.MouseTargetType.GUTTER_LINE_NUMBERS && t.position?.lineNumber && t.element) {
            // Check if the clicked element is specifically the step into button
            if (t.element.classList && t.element.classList.contains('healStepIntoButton')) {
                // Handle step into action - same behavior as "Edit block" context menu
                const activeTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);
                if (activeTab?.metadataManager && openBlockTabFromMetadata) {
                    const metadata = activeTab.metadataManager.getMetadataByLineNumber(t.position.lineNumber);
                    if (metadata && metadata.call.methodName.startsWith('blocks.')) {
                        openBlockTabFromMetadata(metadata);
                    }
                }
                return;
            }
        }
    };

    const executeStepAtLine = (lineNumber: number, getStoryInfo: () => { steps: UIStep[] }) => {
        const activeTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);
        if (!activeTab?.metadataManager) return;

        const currentSteps = getStoryInfo().steps; // Already enriched with blockDefArgs

        // Get the metadata for the current line
        const metadata = activeTab.metadataManager.getMetadataByLineNumber(lineNumber);

        // No check on the "executed" flag as we want to allow running the same step multiple times
        if (!metadata || metadata.running) return;

        const stepIndex = currentSteps.findIndex((step) => step.id === metadata.key);
        if (stepIndex === -1) {
            toast({
                title: 'ERROR: Step not found',
                variant: 'destructive',
            });
            return;
        }

        executeStep?.(currentSteps[stepIndex]);
    };

    const setupContextMenuActions = () => {
        if (!editorRef.current) return;

        const editor = editorRef.current;

        // Set up cursor position change handler to update context for block editing
        editor.onContextMenu((e) => {
            const position = e.target.position;
            if (!position) return;

            // Use refs to get current state values to avoid closure issues
            const currentActiveTabId = activeTabIdRef.current;
            const currentTabs = tabsRef.current;
            const currentActiveTab = currentTabs.find((tab) => tab.id === currentActiveTabId);
            const metadata = currentActiveTab?.metadataManager?.getMetadataByLineNumber(position.lineNumber);
            const isBlock = metadata && metadata.call.methodName.startsWith('blocks.');

            // Update context key for block function
            editor.createContextKey('isBlockFunction', isBlock);
        });

        // Register highlight selector action
        editor.addAction({
            id: 'highlight-selector',
            label: '🌟 Highlight selector',
            contextMenuGroupId: '0_custom_groups',
            contextMenuOrder: 0,
            keybindings: [monaco.KeyMod.CtrlCmd | monaco.KeyCode.Period], // Ctrl+. or Cmd+.
            run: (ed) => {
                showSelector?.();
            },
        });

        // Register edit block action
        editor.addAction({
            id: 'edit-block',
            label: '✏ Edit block',
            contextMenuGroupId: '0_custom_groups',
            contextMenuOrder: 1,
            precondition: 'isBlockFunction',
            run: async (ed) => {
                const position = ed.getPosition();
                if (!position) return;

                const activeTab = tabsRef.current.find((tab) => tab.id === activeTabIdRef.current);
                const metadata = activeTab?.metadataManager?.getMetadataByLineNumber(position.lineNumber);
                if (metadata && metadata.call.methodName.startsWith('blocks.')) {
                    await openBlockTabFromMetadata?.(metadata);
                }
            },
        });
    };

    const updateContextMenuActions = () => {
        if (!editorRef.current) {
            return;
        }

        const editor = editorRef.current;

        // Force update of context keys when tab changes
        // This ensures the context menu shows the correct options for blocks
        const currentActiveTabId = activeTabIdRef.current;
        const currentTabs = tabsRef.current;
        const currentActiveTab = currentTabs.find((tab) => tab.id === currentActiveTabId);

        // Update isBlockFunction context for the current cursor position
        const position = editor.getPosition();
        if (position && currentActiveTab?.metadataManager) {
            const metadata = currentActiveTab.metadataManager.getMetadataByLineNumber(position.lineNumber);
            const isBlock = metadata && metadata.call.methodName.startsWith('blocks.');

            editor.createContextKey('isBlockFunction', isBlock);
        } else {
            editor.createContextKey('isBlockFunction', false);
        }
    };

    return {
        handleEditorKeyDown,
        handleEditorMouseDown,
        executeStepAtLine,
        setupContextMenuActions,
        updateContextMenuActions,
    };
};
